import { throwError } from 'rxjs'
import { ajax } from 'rxjs/ajax'
import { catchError } from 'rxjs/operators'
import { baseURL } from './api-routes.common'

const requestHeader = () => {
  const headers = {
    'Content-Type': 'application/json',
  }
  const userData = JSON.parse(localStorage.getItem('user_info') || '{}')
  let token: string | boolean = false
  if (userData.token) {
    token = userData.token
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  return headers
}

const objToQueryString = (obj = {}) => {
  return Object.keys(obj)
    .filter(
      (key) => obj[key] !== undefined && obj[key] !== null && obj[key] !== '',
    )
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`)
    .join('&')
}

const handleError = (operation: string) => (err: { status: number }) => {
  const errMsg = `Error in ${operation}()`

  if (err.status === 401) {
    localStorage.clear()
    window.location.href = '/auth/login'
  }
  return throwError(() => console.error(errMsg))
}

const request = (method: string, path: string, body?: any) =>
  ajax({
    headers: requestHeader(),
    method,
    withCredentials: false,
    url: `${baseURL}api/v2${path}`,
    body,
  })

const get = (path: string, filters?: Record<string, any>) => {
  const query = filters ? objToQueryString(filters) : ''
  const suffix = query ? `?${query}` : ''
  return request('GET', `${path}${suffix}`)
}

const post = (path: string, body?: any) => request('POST', path, body)
const put = (path: string, body?: any) => request('PUT', path, body)
const del = (path: string) => request('DELETE', path)

export const getCashFlow = (start_date) =>
  get('/accounting/reports/cash-flow', { start_date }).pipe(
    catchError(handleError('getCashFlow')),
  )

export const getProfitLoss = (start_date) =>
  get('/accounting/reports/pnl', { start_date }).pipe(
    catchError(handleError('getProfitLoss')),
  )

export const getTopBalance = () =>
  get('/accounting/reports/top-balances').pipe(
    catchError(handleError('getTopBalance')),
  )

export const getJournals = () =>
  get('/accounting/journals').pipe(catchError(handleError('getJournals')))

export const getJournal = (id) =>
  get(`/accounting/journals/${id}`).pipe(catchError(handleError('getJournal')))

export const createJournal = (data) =>
  post('/accounting/journals', data).pipe(
    catchError(handleError('createJournal')),
  )

export const updateJournal = (id, data) =>
  put(`/accounting/journals/${id}`, data).pipe(
    catchError(handleError('updateJournal')),
  )

export const deleteJournal = (id) =>
  del(`/accounting/journals/${id}`).pipe(
    catchError(handleError('deleteJournal')),
  )

export const getCOAOptions = () =>
  get('/accounting/coa').pipe(catchError(handleError('getCOAOptions')))

export const getCOAFiltersHierarchy = () =>
  get('/accounting/coa/filters-hierarchy').pipe(
    catchError(handleError('getCOAFiltersHierarchy')),
  )

export const getCashFlowDetails = (filters) =>
  get('/accounting/reports/cash-flow/detail', filters).pipe(
    catchError(handleError('getCashFlowDetails')),
  )

export const getTransactionsCategories = () =>
  get('/accounting/transactions/categories').pipe(
    catchError(handleError('getTransactionsCategories')),
  )

export const getPaymentHeads = () =>
  get('/accounting/payment-heads').pipe(
    catchError(handleError('getPaymentHeads')),
  )

export const addCategory = (data) =>
  post('/accounting/accounts', data).pipe(
    catchError(handleError('addCategory')),
  )

export const updateCategory = (id, data) =>
  put(`/accounting/accounts/${id}`, data).pipe(
    catchError(handleError('updateCategory')),
  )

export const deleteCategory = (id) =>
  del(`/accounting/accounts/${id}`).pipe(
    catchError(handleError('deleteCategory')),
  )

export const addTransaction = (payload) =>
  post('/accounting/transactions', payload).pipe(
    catchError(handleError('addTransaction')),
  )

export const updateTransaction = (data, id) =>
  put(`/accounting/transactions/${id}`, data).pipe(
    catchError(handleError('updateTransaction')),
  )

export const deleteTransaction = (id) =>
  del(`/accounting/transactions/${id}`).pipe(
    catchError(handleError('deleteTransaction')),
  )

export const getAllJournalEntries = (filters) =>
  get('/accounting/journal-entries', filters).pipe(
    catchError(handleError('getAllJournalEntries')),
  )

export const getJournalEntry = (id) =>
  get(`/accounting/journal-entries/${id}`, { include: 'lines' }).pipe(
    catchError(handleError('getJournalEntry')),
  )

export const addJournalEntry = (data) =>
  post('/accounting/journal-entries', data).pipe(
    catchError(handleError('addJournalEntry')),
  )

export const updateJournalEntry = (id, data) =>
  put(`/accounting/journal-entries/${id}`, data).pipe(
    catchError(handleError('updateJournalEntry')),
  )

export const deleteJournalEntry = (id) =>
  del(`/accounting/journal-entries/${id}`).pipe(
    catchError(handleError('deleteJournalEntry')),
  )

export const postJournalEntry = (id, payload = {}) =>
  post(`/accounting/journal-entries/${id}/post`, payload).pipe(
    catchError(handleError('postJournalEntry')),
  )

export const reverseJournalEntry = (id, payload = {}) =>
  post(`/accounting/journal-entries/${id}/reverse`, payload).pipe(
    catchError(handleError('reverseJournalEntry')),
  )

export const exportTransactions = (filters) =>
  ajax({
    headers: requestHeader(),
    responseType: 'arraybuffer',
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/accounting/transactions/export?${objToQueryString(
      filters,
    )}`,
  }).pipe(catchError(handleError('getAllTransactions')))

export const getIncomeStatement = (filters) =>
  get('/accounting/reports/income-statement', filters).pipe(
    catchError(handleError('getIncomeStatement')),
  )

export const getTrialBalance = (filters) =>
  get('/accounting/reports/trial-balance', filters).pipe(
    catchError(handleError('getTrialBalance')),
  )

export const getTrailBalance = getTrialBalance

export const getBalanceSheet = (filters) =>
  get('/accounting/reports/balance-sheet', filters).pipe(
    catchError(handleError('getBalanceSheet')),
  )

export const getCashFlowDetail = (filters) =>
  get(
    '/accounting/reports/cash-flow/detail',
    typeof filters === 'object' ? filters : { end_date: filters },
  ).pipe(catchError(handleError('getCashFlowDetail')))

export const downLoadFile = (
  data: BlobPart,
  fileName: string,
  type: string,
) => {
  const blob = new Blob([data], { type })
  const url = window.URL.createObjectURL(blob)
  const downloadLink: HTMLAnchorElement = document.createElement('a')
  downloadLink.href = url
  downloadLink.download = fileName
  document.body.appendChild(downloadLink)
  downloadLink.click()
  document.body.removeChild(downloadLink)
}

export const getChartOfAccounts = (filters = {}) =>
  get('/accounting/accounts', filters).pipe(
    catchError(handleError('getChartOfAccounts')),
  )

export const getPostingAccounts = (filters = {}) =>
  get('/accounting/accounts', { view: 'posting_only', ...filters }).pipe(
    catchError(handleError('getPostingAccounts')),
  )

export const getAccount = (id) =>
  get(`/accounting/accounts/${id}`).pipe(catchError(handleError('getAccount')))

export const getCOAGroups = () =>
  get('/accounting/accounts/groups').pipe(
    catchError(handleError('getCOAGroups')),
  )

export const getAccountingSettings = () =>
  get('/settings/accounting').pipe(
    catchError(handleError('getAccountingSettings')),
  )

export const getAccountingSetupStatus = () =>
  get('/settings/accounting/setup-status').pipe(
    catchError(handleError('getAccountingSetupStatus')),
  )

export const updateAccountingSettings = (payload) =>
  put('/settings/accounting', payload).pipe(
    catchError(handleError('updateAccountingSettings')),
  )

export const enableAccountingDemoMode = (payload = {}) =>
  post('/settings/accounting/demo-mode/enable', payload).pipe(
    catchError(handleError('enableAccountingDemoMode')),
  )

export const disableAccountingDemoMode = (payload) =>
  post('/settings/accounting/demo-mode/disable', payload).pipe(
    catchError(handleError('disableAccountingDemoMode')),
  )

export const startAccountingLiveUsage = (payload) =>
  post('/settings/accounting/demo-mode/start-live-usage', payload).pipe(
    catchError(handleError('startAccountingLiveUsage')),
  )

export const getPeriods = () =>
  get('/accounting/periods').pipe(catchError(handleError('getPeriods')))

export const getOpenPeriods = () =>
  get('/accounting/periods', { status: 'open' }).pipe(
    catchError(handleError('getOpenPeriods')),
  )

export const createPeriod = (payload) =>
  post('/accounting/periods', payload).pipe(
    catchError(handleError('createPeriod')),
  )

export const softClosePeriod = (id) =>
  post(`/accounting/periods/${id}/soft-close`).pipe(
    catchError(handleError('softClosePeriod')),
  )

export const hardClosePeriod = (id) =>
  post(`/accounting/periods/${id}/hard-close`).pipe(
    catchError(handleError('hardClosePeriod')),
  )

export const reopenPeriod = (id) =>
  post(`/accounting/periods/${id}/reopen`).pipe(
    catchError(handleError('reopenPeriod')),
  )

export const getPartners = () =>
  get('/accounting/partners').pipe(catchError(handleError('getPartners')))

export const createPartner = (payload) =>
  post('/accounting/partners', payload).pipe(
    catchError(handleError('createPartner')),
  )

export const updatePartner = (id, payload) =>
  put(`/accounting/partners/${id}`, payload).pipe(
    catchError(handleError('updatePartner')),
  )

export const deletePartner = (id) =>
  del(`/accounting/partners/${id}`).pipe(
    catchError(handleError('deletePartner')),
  )

export const getCostCenters = () =>
  get('/accounting/cost-centers').pipe(
    catchError(handleError('getCostCenters')),
  )

export const createCostCenter = (payload) =>
  post('/accounting/cost-centers', payload).pipe(
    catchError(handleError('createCostCenter')),
  )

export const updateCostCenter = (id, payload) =>
  put(`/accounting/cost-centers/${id}`, payload).pipe(
    catchError(handleError('updateCostCenter')),
  )

export const deleteCostCenter = (id) =>
  del(`/accounting/cost-centers/${id}`).pipe(
    catchError(handleError('deleteCostCenter')),
  )

export const getExpenses = () =>
  get('/accounting/expenses').pipe(catchError(handleError('getExpenses')))

export const submitExpense = (id) =>
  post(`/accounting/expenses/${id}/submit`).pipe(
    catchError(handleError('submitExpense')),
  )

export const approveExpense = (id) =>
  post(`/accounting/expenses/${id}/approve`).pipe(
    catchError(handleError('approveExpense')),
  )

export const refuseExpense = (id) =>
  post(`/accounting/expenses/${id}/refuse`).pipe(
    catchError(handleError('refuseExpense')),
  )

export const payExpense = (id, payload = {}) =>
  post(`/accounting/expenses/${id}/pay`, payload).pipe(
    catchError(handleError('payExpense')),
  )

export const getGeneralLedger = (filters) =>
  get('/accounting/reports/general-ledger', filters).pipe(
    catchError(handleError('getGeneralLedger')),
  )

export const getAccountBalances = (filters) =>
  get('/accounting/reports/account-balances', filters).pipe(
    catchError(handleError('getAccountBalances')),
  )

export const getExpenseRegister = (filters) =>
  get('/accounting/reports/expense-register', filters).pipe(
    catchError(handleError('getExpenseRegister')),
  )

export const getCashFlowReport = (filters) =>
  get('/accounting/reports/cash-flow', filters).pipe(
    catchError(handleError('getCashFlowReport')),
  )

export const getInvoiceRegister = (filters) =>
  get('/accounting/reports/invoice-register', filters).pipe(
    catchError(handleError('getInvoiceRegister')),
  )

export const getPaymentRegister = (filters) =>
  get('/accounting/reports/payment-register', filters).pipe(
    catchError(handleError('getPaymentRegister')),
  )

export const getPartnerBalances = (filters) =>
  get('/accounting/reports/partner-balances', filters).pipe(
    catchError(handleError('getPartnerBalances')),
  )

export const getReceivableAging = (filters) =>
  get('/accounting/reports/receivable-aging', filters).pipe(
    catchError(handleError('getReceivableAging')),
  )

export const getPayableAging = (filters) =>
  get('/accounting/reports/payable-aging', filters).pipe(
    catchError(handleError('getPayableAging')),
  )

export const getFinanceInvoices = (filters) =>
  get('/accounting/invoices', filters).pipe(
    catchError(handleError('getInvoices')),
  )

export const getFinanceInvoice = (id) =>
  get(`/accounting/invoices/${id}`).pipe(catchError(handleError('getInvoice')))

export const createFinanceInvoice = (payload) =>
  post('/accounting/invoices', payload).pipe(
    catchError(handleError('createFinanceInvoice')),
  )

export const updateFinanceInvoice = (id, payload) =>
  put(`/accounting/invoices/${id}`, payload).pipe(
    catchError(handleError('updateInvoice')),
  )

export const deleteFinanceInvoice = (id) =>
  del(`/accounting/invoices/${id}`).pipe(
    catchError(handleError('deleteInvoice')),
  )

export const confirmFinanceInvoice = (id) =>
  post(`/accounting/invoices/${id}/confirm`).pipe(
    catchError(handleError('confirmInvoice')),
  )

export const postFinanceInvoice = (id, payload = {}) =>
  post(`/accounting/invoices/${id}/post`, payload).pipe(
    catchError(handleError('postInvoice')),
  )

export const reverseFinanceInvoice = (id, payload = {}) =>
  post(`/accounting/invoices/${id}/reverse`, payload).pipe(
    catchError(handleError('reverseInvoice')),
  )

export const cancelFinanceInvoice = (id) =>
  post(`/accounting/invoices/${id}/cancel`).pipe(
    catchError(handleError('cancelInvoice')),
  )

export const getVendorBills = (filters) =>
  get('/accounting/vendor-bills', filters).pipe(
    catchError(handleError('getVendorBills')),
  )

export const getVendorBill = (id) =>
  get(`/accounting/vendor-bills/${id}`).pipe(
    catchError(handleError('getVendorBill')),
  )

export const createVendorBill = (payload) =>
  post('/accounting/vendor-bills', payload).pipe(
    catchError(handleError('createVendorBill')),
  )

export const updateVendorBill = (id, payload) =>
  put(`/accounting/vendor-bills/${id}`, payload).pipe(
    catchError(handleError('updateVendorBill')),
  )

export const deleteVendorBill = (id) =>
  del(`/accounting/vendor-bills/${id}`).pipe(
    catchError(handleError('deleteVendorBill')),
  )

export const confirmVendorBill = (id) =>
  post(`/accounting/vendor-bills/${id}/confirm`).pipe(
    catchError(handleError('confirmVendorBill')),
  )

export const postVendorBill = (id, payload = {}) =>
  post(`/accounting/vendor-bills/${id}/post`, payload).pipe(
    catchError(handleError('postVendorBill')),
  )

export const reverseVendorBill = (id, payload = {}) =>
  post(`/accounting/vendor-bills/${id}/reverse`, payload).pipe(
    catchError(handleError('reverseVendorBill')),
  )

export const cancelVendorBill = (id) =>
  post(`/accounting/vendor-bills/${id}/cancel`).pipe(
    catchError(handleError('cancelVendorBill')),
  )

export const getPayments = (filters) =>
  get('/accounting/payments', filters).pipe(
    catchError(handleError('getPayments')),
  )

export const getPayment = (id) =>
  get(`/accounting/payments/${id}`).pipe(catchError(handleError('getPayment')))

export const createPayment = (payload) =>
  post('/accounting/payments', payload).pipe(
    catchError(handleError('createPayment')),
  )

export const updatePayment = (id, payload) =>
  put(`/accounting/payments/${id}`, payload).pipe(
    catchError(handleError('updatePayment')),
  )

export const deleteFinancePayment = (id) =>
  del(`/accounting/payments/${id}`).pipe(
    catchError(handleError('deletePayment')),
  )

export const postPaymentDocument = (id, payload = {}) =>
  post(`/accounting/payments/${id}/post`, payload).pipe(
    catchError(handleError('postPaymentDocument')),
  )

export const reversePaymentDocument = (id, payload = {}) =>
  post(`/accounting/payments/${id}/reverse`, payload).pipe(
    catchError(handleError('reversePaymentDocument')),
  )

export const allocatePayment = (id, payload) =>
  post(`/accounting/payments/${id}/allocate`, payload).pipe(
    catchError(handleError('allocatePayment')),
  )

export const getReconciliations = (filters) =>
  get('/accounting/reconciliations', filters).pipe(
    catchError(handleError('getReconciliations')),
  )

export const getReconciliation = (id) =>
  get(`/accounting/reconciliations/${id}`).pipe(
    catchError(handleError('getReconciliation')),
  )

export const undoReconciliation = (id) =>
  post(`/accounting/reconciliations/${id}/undo`).pipe(
    catchError(handleError('undoReconciliation')),
  )

export const getDemoDataBatches = () =>
  get('/accounting/admin/demo-data/batches').pipe(
    catchError(handleError('getDemoDataBatches')),
  )

export const getFinanceDemoDataStatus = () =>
  get('/accounting/admin/demo-data/status').pipe(
    catchError(handleError('getFinanceDemoDataStatus')),
  )

export const getDemoDataStatus = () =>
  getFinanceDemoDataStatus().pipe(catchError(handleError('getDemoDataStatus')))

export const installFinanceDemoData = (payload) =>
  post('/accounting/admin/demo-data/install', payload).pipe(
    catchError(handleError('installFinanceDemoData')),
  )

export const installDemoData = (payload) =>
  installFinanceDemoData(payload).pipe(
    catchError(handleError('installDemoData')),
  )

export const removeFinanceDemoBatch = (payload) =>
  post('/accounting/admin/demo-data/remove', payload).pipe(
    catchError(handleError('removeFinanceDemoBatch')),
  )

export const removeDemoData = (payload) =>
  removeFinanceDemoBatch(payload).pipe(
    catchError(handleError('removeDemoData')),
  )

export const resetFinanceDemoData = (payload) =>
  post('/accounting/admin/demo-data/reset', payload).pipe(
    catchError(handleError('resetFinanceDemoData')),
  )
